import {
  chakra,
  useMenuList,
  forwardRef,
  useMenuPositioner,
  useMultiStyleConfig,
  MenuList as ChakraMenuList,
  MenuListProps as ChakraMenuListProps
} from '@chakra-ui/react';
import { cx, mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';

import { InferPropTypes } from '../../types';

import DisplayNames from './enums';

const propTypes = {
  enableAnimations: PropTypes.bool
};
const defaultProps = {
  enableAnimations: false
};

export type MenuListProps = ChakraMenuListProps & InferPropTypes<typeof propTypes>;

type HTMLAttributes = React.HTMLAttributes<HTMLElement>;

/*
  ! NOTE: This component is forked from chakra UI in combination with a community sandbox custom MenuList.
  ? As of 11/05/2021 disabling animations is not supported in Chakra: https://github.com/chakra-ui/chakra-ui/issues/3696
  ? See discord community messages: https://discord.com/channels/660863154703695893/660865137137156146/898621338254708777
  ? For reference; this is the community sandbox example of a custom MenuList: https://codesandbox.io/s/cool-https-hqx57?file=/src/components/MenuListNoMotion.js:0-799
*/
const MenuList = forwardRef<MenuListProps, 'div'>((props, ref) => {
  const { enableAnimations, __css, ...chakraProps } = props;
  const { rootProps, ...rest } = chakraProps;

  const menuListProps = useMenuList(rest, ref) as HTMLAttributes;
  const positionerProps = useMenuPositioner(rootProps);

  const styles = useMultiStyleConfig('Menu', menuListProps);

  return enableAnimations ? (
    <ChakraMenuList {...chakraProps} data-testid="menu-list-with-motion" />
  ) : (
    <chakra.div
      {...positionerProps}
      __css={{ zIndex: props.zIndex ?? styles.list?.zIndex }}
      data-testid="menu-list-without-motion"
    >
      <chakra.div
        {...menuListProps}
        __css={mergeWith(
          {
            outline: 0,
            ...styles.list
          },
          __css
        )}
        className={cx('chakra-menu__menu-list-no-motion', menuListProps.className)}
      />
    </chakra.div>
  );
});

MenuList.propTypes = propTypes;
MenuList.defaultProps = defaultProps;
MenuList.displayName = DisplayNames.MenuList;

export default MenuList;
